/** Minimal inline SVG icon set (no icon dependency). */
import type { ReactNode } from 'react';

export type IconName =
  | 'chat'
  | 'approvals'
  | 'todo'
  | 'alarm'
  | 'calendar'
  | 'sessions'
  | 'settings'
  | 'device'
  | 'send'
  | 'stop'
  | 'lock'
  | 'unlock'
  | 'refresh'
  | 'plus'
  | 'back'
  | 'check'
  | 'close'
  | 'shield'
  | 'spark'
  | 'scan'
  | 'trash'
  | 'chevron';

const PATHS: Record<IconName, ReactNode> = {
  chat: <path d="M4 5h16v10H9l-5 4V5Z" />,
  approvals: (
    <>
      <path d="M9 4h6l1 2h3v15H5V6h3l1-2Z" />
      <path d="m9 13 2 2 4-4" />
    </>
  ),
  todo: (
    <>
      <path d="M10 6h10M10 12h10M10 18h10" />
      <path d="m4 6 1.6 1.6L8 5.2M4 12l1.6 1.6L8 11.2M4 18l1.6 1.6L8 17.2" />
    </>
  ),
  alarm: (
    <>
      <circle cx="12" cy="13" r="7" />
      <path d="M12 9v4l2.5 1.5M5 4l2 2M19 4l-2 2" />
    </>
  ),
  calendar: (
    <>
      <rect x="4" y="5" width="16" height="15" rx="2" />
      <path d="M8 3v4M16 3v4M4 10h16" />
      <path d="M8 14h3M14 14h2M8 17h2" />
    </>
  ),
  sessions: (
    <>
      <rect x="4" y="4" width="16" height="16" rx="2" />
      <path d="M8 9h8M8 13h8M8 17h5" />
    </>
  ),
  settings: (
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M19 12a7 7 0 0 0-.1-1l2-1.5-2-3.4-2.3 1a7 7 0 0 0-1.7-1L14.5 3h-5l-.4 2.6a7 7 0 0 0-1.7 1l-2.3-1-2 3.4L5 11a7 7 0 0 0 0 2l-2 1.5 2 3.4 2.3-1a7 7 0 0 0 1.7 1L9.5 21h5l.4-2.6a7 7 0 0 0 1.7-1l2.3 1 2-3.4-2-1.5c.06-.33.1-.66.1-1Z" />
    </>
  ),
  device: (
    <>
      <rect x="7" y="3" width="10" height="18" rx="2" />
      <path d="M11 18h2" />
    </>
  ),
  send: <path d="m4 12 16-8-6 16-2.5-6.5L4 12Z" />,
  stop: <rect x="7" y="7" width="10" height="10" rx="2" />,
  lock: (
    <>
      <rect x="5" y="11" width="14" height="9" rx="2" />
      <path d="M8 11V8a4 4 0 0 1 8 0v3" />
    </>
  ),
  unlock: (
    <>
      <rect x="5" y="11" width="14" height="9" rx="2" />
      <path d="M8 11V8a4 4 0 0 1 7-2.6" />
    </>
  ),
  refresh: (
    <>
      <path d="M20 11a8 8 0 1 0-2.3 6.3" />
      <path d="M20 5v6h-6" />
    </>
  ),
  plus: <path d="M12 5v14M5 12h14" />,
  back: <path d="m14 6-6 6 6 6" />,
  check: <path d="m5 12 5 5L19 7" />,
  close: <path d="m6 6 12 12M18 6 6 18" />,
  shield: (
    <>
      <path d="M12 3 5 6v6c0 4 3 7.5 7 9 4-1.5 7-5 7-9V6l-7-3Z" />
      <path d="m9 12 2 2 4-4" />
    </>
  ),
  spark: <path d="m12 3 1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8L12 3Z" />,
  scan: (
    <>
      <path d="M4 9V5h4M20 9V5h-4M4 15v4h4M20 15v4h-4" />
      <path d="M4 12h16" />
    </>
  ),
  trash: (
    <>
      <path d="M5 7h14M9 7V5h6v2M7 7l1 13h8l1-13" />
      <path d="M10 11v6M14 11v6" />
    </>
  ),
  chevron: <path d="m9 6 6 6-6 6" />,
};

export function Icon({ name, className = 'h-5 w-5' }: { name: IconName; className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      {PATHS[name]}
    </svg>
  );
}
